import { useFieldListQuery } from '@client/graphql/__generated__/document-operations';
import {
  FieldType,
  UsState,
  VariableDataType,
  VariableSource,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import { AutocompleteVariables } from './types';

export const FIELD_TO_VARIABLE_DATA_TYPE = {
  [FieldType.SELECT]: VariableDataType.LIST,
  [FieldType.CHECKBOX]: VariableDataType.BOOLEAN,
  [FieldType.DATE]: VariableDataType.TEXT,
  [FieldType.IMAGE]: VariableDataType.TEXT,
  [FieldType.INITIAL]: VariableDataType.TEXT,
  [FieldType.NUMBER]: VariableDataType.NUMBER,
  [FieldType.RADIO]: VariableDataType.BOOLEAN,
  [FieldType.SIGNATURE]: VariableDataType.TEXT,
  [FieldType.TEXT]: VariableDataType.TEXT,
  [FieldType.TEXTAREA]: VariableDataType.TEXT,
  [FieldType.SIGNED_DATE]: VariableDataType.TEXT,
};

export const useFieldAutocomplete = (
  variableSources: VariableSource[],
  documentVersionId?: string,
  usState?: UsState
) => {
  const { data } = useFieldListQuery({
    variables: {
      // As of 2025-07-29 we have ~15k fields, so give ourselves some buffer.
      // We should probably add some way to just fetch all pages
      perPage: 50_000,
      filters: {
        documentVersionId,
        usState: usState ? usState : undefined,
      },
    },
    fetchPolicy: 'cache-first',
    skip: !variableSources.includes(VariableSource.FIELD),
  });

  const autocomplete = useMemo(() => {
    if (!data?.fieldList?.results) {
      return [];
    }

    const fieldList: AutocompleteVariables[] = data?.fieldList?.results?.map(
      (field) => ({
        type: FIELD_TO_VARIABLE_DATA_TYPE[field.fieldType],
        key: field.mappingKey,
        source: VariableSource.FIELD,
        caption: `${field.mappingKey} - ${field.fieldType}`,
        value: `"${field.mappingKey}"`,
        score: 1,
        meta: 'Fields',
      })
    );

    return fieldList;
  }, [data?.fieldList?.results]);

  return autocomplete;
};
